import { AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { QueryService } from '../../comun/services/query.service';
import { GlobalConstants } from '../../global-constants';

@Component({
  selector: 'app-huellas-modal',
  templateUrl: './huellas-modal.component.html',
  styleUrl: './huellas-modal.component.css'
})
export class HuellasModalComponent implements AfterViewInit, OnDestroy {
  @Input() userid!: string;
  @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLDivElement>;

  global: any = GlobalConstants;
  huellas: any[] = [];
  rs_id = 0;
  load = false;
  loadingMore = false;
  hasMore = true;
  error = '';
  private modalElement: HTMLElement | null = null;
  private request?: Subscription;
  private moreRequest?: Subscription;
  private lastCursor = '';

  constructor(private query: QueryService, private zone: NgZone) {}

  private shownHandler = (event: Event): void => {
    this.zone.run(() => {
      const trigger = (event as any).relatedTarget as HTMLElement | null;
      const id = Number(trigger?.getAttribute('data-id') || 0);
      this.rs_id = Number.isFinite(id) && id > 0 ? id : 0;
      this.huellas = [];
      this.lastCursor = '';
      this.hasMore = true;
      this.error = '';
      this.request?.unsubscribe();
      this.moreRequest?.unsubscribe();
      this.getHuellas();
    });
  };

  private hiddenHandler = (): void => {
    this.zone.run(() => {
      this.request?.unsubscribe();
      this.moreRequest?.unsubscribe();
      this.load = false;
      this.loadingMore = false;
      this.huellas = [];
      // No recargar la página: interrumpiría la navegación y otros widgets Angular.
    });
  };

  ngAfterViewInit(): void {
    this.modalElement = document.getElementById('huellasModal');
    this.modalElement?.addEventListener('shown.bs.modal', this.shownHandler);
    this.modalElement?.addEventListener('hidden.bs.modal', this.hiddenHandler);
  }

  ngOnDestroy(): void {
    this.modalElement?.removeEventListener('shown.bs.modal', this.shownHandler);
    this.modalElement?.removeEventListener('hidden.bs.modal', this.hiddenHandler);
    this.request?.unsubscribe();
    this.moreRequest?.unsubscribe();
  }

  getHuellas(): void {
    this.load = true;
    this.error = '';
    this.request = this.query.query('huellas', 'GET', { rs_id: this.rs_id }).subscribe({
      next: (resultado: any) => {
        if (resultado?.resultado === 'ERROR') {
          this.error = resultado.mensaje || 'No se pudieron cargar las huellas.';
          this.huellas = [];
          this.hasMore = false;
        } else {
          this.huellas = Array.isArray(resultado?.registros) ? resultado.registros : (Array.isArray(resultado) ? resultado : []);
          this.hasMore = this.huellas.length > 0;
        }
        this.load = false;
      },
      error: () => { this.error = 'No se pudieron cargar las huellas. Inténtalo de nuevo.'; this.load = false; }
    });
  }

  private identity(item: any): string {
    // Una huella es única por publicación/perfil + autor; la API puede no entregar ID.
    return String(item.hu_id ?? `${item.hu_tipo || ''}:${item.hu_tipo_id ?? this.rs_id}:${item.hu_us_id ?? item.ID ?? item.slug ?? ''}:${item.hu_fechahora_bd ?? item.hu_fechahora ?? ''}`);
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 130) this.cargarHuellasSiguientes();
  }

  cargarHuellasSiguientes(): void {
    if (this.load || this.loadingMore || !this.hasMore || !this.huellas.length) return;
    const ultimo = this.huellas[this.huellas.length - 1];
    const cursor = String(ultimo?.hu_fechahora_bd || ultimo?.hu_fechahora || '');
    if (!cursor || cursor === this.lastCursor) { this.hasMore = false; return; }
    this.loadingMore = true;
    this.lastCursor = cursor;
    this.moreRequest = this.query.query('huellas', 'GET', {
      rs_id: this.rs_id,
      fecha_siguiente: cursor
    }).subscribe({
      next: (resultado: any) => {
        const nuevos: any[] = Array.isArray(resultado?.registros) ? resultado.registros : (Array.isArray(resultado) ? resultado : []);
        const existentes = new Set(this.huellas.map(item => this.identity(item)));
        const unicos = nuevos.filter(item => !existentes.has(this.identity(item)) && (existentes.add(this.identity(item)), true));
        this.huellas = [...this.huellas, ...unicos];
        // Si la API devuelve siempre la misma página, no seguir haciendo solicitudes.
        this.hasMore = nuevos.length > 0 && unicos.length > 0;
        this.loadingMore = false;
      },
      error: () => { this.loadingMore = false; this.hasMore = false; }
    });
  }

  goPerfil(perfil: any): void {
    const slug = String(perfil?.slug ?? '').trim();
    if (!slug) return;
    window.open(GlobalConstants.urlServer + 'perfil/' + encodeURIComponent(slug), '_blank', 'noopener,noreferrer');
  }

  trackByHuella = (_index: number, huella: any): string => this.identity(huella);

  nombre(huella: any): string {
    return [huella?.mascota?.ma_nombre, huella?.first_name].filter(Boolean).join(' y ') || 'Miembro de iGuau';
  }

  detalle(huella: any): string {
    return [huella?.mascota?.ma_sexo, huella?.mascota?.edad, huella?.sexo, huella?.edad]
      .filter(Boolean).join(' · ');
  }
}
